import cx from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@sb/design-system';
import { FeedbackTag } from '@sb/feathers-types';

import { CHAT_PANEL_CARD_ID } from './chatPanelConstants';
import { FeedbackQuoteBlock } from './FeedbackQuoteBlock';
import { describeSpeedup, formatMinutes } from './taskTiming';
import { TaskTimingPanel, VERDICT_COLOR } from './TaskTimingPanel';

export type FeedbackSentiment = 'positive' | 'negative';

export interface FeedbackPayload {
  sessionID: string;
  messageID: string;
  sentiment: FeedbackSentiment;
  content?: string;
  /** Tags picked in the reason modal; empty when none were. */
  tags: FeedbackTag[];
  /** Text selected in the reply before the thumb was tapped. */
  quote?: string;
}

/** The tag a thumbs-up starts with; the operator can still change it. */
const POSITIVE_DEFAULT_TAG: FeedbackTag = 'Worked well';

/**
 * Each thumb shows the tags that can apply to it. "Suggestion" is in both:
 * a good answer can still prompt an idea, and so can a bad one.
 */
export const POSITIVE_TAGS: readonly FeedbackTag[] = [
  'Worked well',
  'Suggestion',
];

export const NEGATIVE_TAGS: readonly FeedbackTag[] = FeedbackTag.options.filter(
  (tag) => tag !== 'Worked well',
);

/** What the per-reply clock needs to know about its message. */
export interface MessageTiming {
  /**
   * Whether an estimate can be logged here right now. The mount allows it
   * on the newest reply, or on an older reply whose timing card was already
   * opened, so an in-progress estimate survives the next reply arriving.
   */
  canLog: boolean;
  suggestedMinutes?: number;
  /** The estimate already logged for this reply, if any. */
  recorded: {
    minutesByHand: number;
    minutesWithAgent: number;
    taskName: string | null;
  } | null;
  onLog: (
    minutesByHand: number,
    minutesWithAgent: number,
    taskName: string | null,
  ) => Promise<void>;
  /** Keeps an opened draft loggable if a newer reply arrives. */
  onOpenChange?: (isOpen: boolean) => void;
}

/** Longest quote kept from a selection; matches the column's zod cap. */
const QUOTE_MAX_CHARS = 1000;

/** How much of the quote the inline preview shows before eliding. */
const QUOTE_PREVIEW_CHARS = 48;

/** Callback ref that scrolls a just-opened card into view. */
const revealOnMount = (el: HTMLDivElement | null): void => {
  // jsdom has no scrollIntoView; the guard keeps the card mountable there.
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest' });
  }
};

function elide(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

interface MessageFeedbackProps {
  sessionID: string;
  messageID: string;
  /**
   * Position of this thumbs row in the transcript, used only to keep the
   * thumb testids unique — one row renders per assistant message, and
   * duplicate testids break Playwright's strict-mode locators. Not derived
   * from the message id: testids must not carry server-generated data.
   */
  index: number;
  /**
   * Fires once the user has both picked a sentiment AND submitted (or
   * skipped) the reason modal. Cancelling the modal clears the
   * pending pick and does not fire the callback — the picked thumb
   * only takes on its verdict colour after explicit submission. The mount wires
   * this into the feathers `agent-feedback` create.
   *
   * May return a promise: a rejection un-submits the thumbs, so a failed
   * write leaves the message rated exactly as the server has it (un-rated)
   * and the user can try again. A void return is treated as success.
   */
  onSubmit: (payload: FeedbackPayload) => void | Promise<void>;
  /**
   * Verdict already recorded for this message — an `agentFeedback` row
   * loaded for the session, or one submitted earlier in this mount.
   * Renders the thumbs in their submitted (locked, colored) state so a
   * rating survives leaving the chat and coming back.
   */
  submittedFeedback?: SubmittedFeedback | null;
  /**
   * When supplied, a clock joins the thumbs. Tapping it opens the task
   * timing card under this reply; once logged, the clock locks and shows
   * the result. Omitted for replies that can neither log nor show one.
   */
  timing?: MessageTiming;
}

/**
 * A submitted thumb takes the colour of its verdict — green for approval,
 * red for a complaint — so the recorded sentiment is readable at a glance
 * without re-opening the message. Both tokens are theme-aware.
 */
const ACTIVE_COLOR: Record<FeedbackSentiment, string> = {
  positive: 'var(--green)',
  negative: 'var(--red)',
};

const IDLE_COLOR = 'var(--label-quaternary)';

/**
 * The thumb's colour is routed through this custom property instead of being
 * assigned to `color` directly. jsdom's CSS parser rejects `var()` as a
 * `<color>` and silently drops the declaration, but stores custom properties
 * verbatim — so this indirection is what lets a unit test observe that the
 * active colour is actually indexed by sentiment.
 */
const THUMB_COLOR_PROP = '--feedback-thumb-color';

export interface SubmittedFeedback {
  sentiment: FeedbackSentiment;
  reason?: string;
  tags?: FeedbackTag[];
  quote?: string;
}

/**
 * Resolve the portal target once when the modal opens. The chat card
 * is mounted for the lifetime of every consumer instance of
 * `MessageFeedback`, so a single lookup is sufficient — repeating it
 * on every keystroke (`reason` state updates) is wasteful.
 */
function usePanelPortalTarget(active: boolean): HTMLElement | null {
  return useMemo(() => {
    if (!active) return null;
    if (typeof document === 'undefined') return null;

    return document.getElementById(CHAT_PANEL_CARD_ID);
  }, [active]);
}

/**
 * Render the modal into the chat card via portal when the target is
 * resolved; fall back to inline rendering otherwise so tests and
 * storybook (which don't mount the card) still display the modal.
 */
function renderModalIntoPanel(
  target: HTMLElement | null,
  node: React.ReactNode,
): React.ReactNode {
  if (target === null) return node;

  return createPortal(node, target);
}

/**
 * Thumbs-up / thumbs-down row beneath an assistant message. The
 * follow-up modal is portalled into the chat panel itself
 * (not the viewport) so its backdrop only dims the chat surface;
 * the thumbs stay grey until the user submits the modal so a
 * misclick can be cancelled without recording feedback.
 */
export function MessageFeedback({
  sessionID,
  messageID,
  index,
  onSubmit,
  submittedFeedback = null,
  timing,
}: MessageFeedbackProps) {
  const [isTimingOpen, setIsTimingOpen] = useState(false);
  // The thumb is staged when the user clicks it (modal opens) and
  // promoted to `submitted` only after the modal's Submit button
  // fires. Cancelling clears the pending pick.
  const [pending, setPending] = useState<FeedbackSentiment | null>(null);

  const [locallySubmitted, setLocallySubmitted] =
    useState<SubmittedFeedback | null>(null);

  const [reason, setReason] = useState('');
  const [tags, setTags] = useState<FeedbackTag[]>([]);

  // Text the operator selected inside this reply. Captured from the
  // document selection while nothing is recorded yet; shown beside the
  // thumbs so it is clear the rating will carry it, and cleared with ×.
  const [quote, setQuote] = useState<string | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  // A verdict submitted in this mount is newer than whatever the
  // caller loaded, so it wins; otherwise the persisted row drives the
  // display. Kept as state rather than seeded into it so a row that
  // arrives after mount (the session's feedback loads asynchronously)
  // still lands.
  const submitted = locallySubmitted ?? submittedFeedback;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resolve the in-panel portal target once per modal-open. Avoids a
  // synchronous `document.getElementById` on every keystroke (every
  // `setReason` re-renders the component).
  const portalTarget = usePanelPortalTarget(pending !== null);

  // Auto-focus the textarea ~50ms after the modal opens so the user
  // can type their reason immediately. The short delay lets the
  // entrance animation start before focus pulls attention.
  useEffect(() => {
    if (pending === null) return undefined;

    const handle = setTimeout(() => textareaRef.current?.focus(), 50);

    return () => clearTimeout(handle);
  }, [pending]);

  // Dismiss the modal on Escape. The backdrop div has no `tabIndex`
  // so it never receives keyboard focus — an `onKeyDown` on the div
  // would never fire. Listen at the window instead while the modal
  // is open. `stopPropagation` keeps the keystroke from also closing
  // the surrounding chat panel.
  useEffect(() => {
    if (pending === null) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setPending(null);
      setReason('');
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pending]);

  // Watch the document selection and keep the part that lands inside this
  // reply. The thumbs row is rendered inside the reply's bubble, so the
  // bubble is the row's parent; a selection anchored anywhere in it, but
  // not in the row itself, is a quote of the reply.
  useEffect(() => {
    if (submitted !== null) return undefined;

    const bubble = rowRef.current?.parentElement;

    if (!bubble || typeof document === 'undefined') return undefined;

    const handleSelectionChange = () => {
      const selection = window.getSelection();

      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        return;
      }

      const anchor = selection.anchorNode;

      if (!anchor || !bubble.contains(anchor)) return;
      if (rowRef.current?.contains(anchor)) return;

      // Read the text off the range: Selection.toString() is empty in
      // some DOM implementations even when a range is set.
      const text = selection.getRangeAt(0).toString().trim();

      if (text.length > 0) setQuote(text.slice(0, QUOTE_MAX_CHARS));
    };

    document.addEventListener('selectionchange', handleSelectionChange);

    return () =>
      document.removeEventListener('selectionchange', handleSelectionChange);
  }, [submitted]);

  const handleThumbClick = useCallback(
    (next: FeedbackSentiment) => {
      // After the user has already submitted feedback for this
      // message, the thumbs are locked — re-clicking does nothing.
      if (submitted !== null) return;
      setPending(next);
      setReason('');
      setTags(next === 'positive' ? [POSITIVE_DEFAULT_TAG] : []);
    },
    [submitted],
  );

  const handleCancel = useCallback(() => {
    setPending(null);
    setReason('');
    setTags([]);
  }, []);

  const toggleTag = useCallback((tag: FeedbackTag) => {
    setTags((current) =>
      current.includes(tag)
        ? current.filter((t) => t !== tag)
        : [...current, tag],
    );
  }, []);

  const handleSubmit = useCallback(() => {
    if (pending === null) return;

    // The modal can outlive the moment the message became rated: a
    // persisted verdict for this message can land (the session's feedback
    // loads asynchronously) while the reason modal is still open. Without
    // this the user would overwrite it locally and fire a create the
    // server rejects — `agentFeedback` is UNIQUE on messageID.
    if (submitted !== null) {
      setPending(null);
      setReason('');
      setTags([]);

      return;
    }

    const trimmed = reason.trim();
    const content = trimmed.length > 0 ? trimmed : undefined;
    const quoteToSend = quote ?? undefined;

    setLocallySubmitted({
      sentiment: pending,
      reason: content,
      tags,
      quote: quoteToSend,
    });

    setPending(null);
    setReason('');
    setTags([]);
    setQuote(null);

    const result = onSubmit({
      sessionID,
      messageID,
      sentiment: pending,
      content,
      tags,
      quote: quoteToSend,
    });

    // Un-submit if the write failed, so the thumbs keep showing what is
    // actually persisted rather than a verdict that never landed — and
    // so the user can retry.
    Promise.resolve(result).catch(() => {
      setLocallySubmitted(null);
    });
  }, [pending, reason, tags, quote, submitted, onSubmit, sessionID, messageID]);

  const recordedTiming = timing?.recorded ?? null;
  const showClock = timing !== undefined && (timing.canLog || recordedTiming);

  useEffect(() => {
    if (!isTimingOpen || (timing?.canLog && recordedTiming === null)) return;

    setIsTimingOpen(false);
    timing?.onOpenChange?.(false);
  }, [isTimingOpen, recordedTiming, timing]);

  const recordedTimingSummary = recordedTiming
    ? describeSpeedup(
        recordedTiming.minutesByHand,
        recordedTiming.minutesWithAgent,
      )
    : null;

  const handleTimingLog = useCallback(
    (
      minutesByHand: number,
      minutesWithAgent: number,
      taskName: string | null,
    ) => {
      if (timing === undefined) return Promise.resolve();

      return timing
        .onLog(minutesByHand, minutesWithAgent, taskName)
        .then(() => {
          setIsTimingOpen(false);
          timing.onOpenChange?.(false);
        });
    },
    [timing],
  );

  // Anything already to the right of the thumbs: a pending quote, or the
  // recorded tags and reason. When present the clock moves to its own row.
  const hasTrailing =
    (submitted === null && quote !== null) ||
    Boolean(submitted?.quote) ||
    (submitted?.tags !== undefined && submitted.tags.length > 0) ||
    Boolean(submitted?.reason);

  const timingControls = showClock ? (
    <>
      <button
        type="button"
        data-testid={`chat-feedback-timing-button-${index}`}
        aria-label={
          recordedTiming ? 'Task time logged' : 'Log how long this task took'
        }
        aria-pressed={isTimingOpen}
        aria-disabled={recordedTiming !== null}
        title={
          recordedTiming
            ? 'Task time logged for this reply.'
            : 'Log how long this task took by hand vs with the agent'
        }
        onClick={() => {
          if (recordedTiming !== null) return;

          const nextOpen = !isTimingOpen;

          setIsTimingOpen(nextOpen);
          timing?.onOpenChange?.(nextOpen);
        }}
        className={cx(
          'tw-flex',
          'tw-items-center',
          'tw-justify-center',
          'tw-shrink-0',
          'tw-w-24',
          'tw-h-24',
          'tw-rounded-6',
          'tw-border-none',
          'tw-p-0',
          isTimingOpen ? 'tw-bg-fill-primary' : 'tw-bg-transparent',
          recordedTiming === null && 'hover:tw-bg-fill-primary',
        )}
        style={{
          cursor: recordedTiming === null ? 'pointer' : 'default',
          color:
            recordedTiming !== null || isTimingOpen
              ? 'var(--label-secondary)'
              : IDLE_COLOR,
          transition: 'color 0.15s ease, background 0.15s ease',
        }}
      >
        <Icon
          kind="clock"
          style={{ width: 14, height: 14, color: 'currentColor' }}
        />
      </button>
      {recordedTiming && recordedTimingSummary && (
        <span
          data-testid={`chat-feedback-timing-text-${index}`}
          className={cx(
            'tw-inline-flex',
            'tw-items-center',
            'tw-gap-6',
            'tw-rounded-full',
            'tw-flex-wrap',
            'tw-min-w-0',
          )}
          style={{
            // 12px chip text and the 22px pill aren't in the scales.
            fontSize: 12,
            minHeight: 22,
            padding: '0 9px',
            background: 'var(--fill-primary)',
            color: 'var(--label-secondary)',
            maxWidth: '100%',
          }}
          title={recordedTiming.taskName ?? undefined}
        >
          {recordedTiming.taskName && (
            <span
              className="tw-overflow-hidden tw-text-ellipsis"
              style={{ maxWidth: 120 }}
            >
              {recordedTiming.taskName}
            </span>
          )}
          {recordedTiming.taskName && <span aria-hidden="true">·</span>}
          <span>
            {formatMinutes(recordedTiming.minutesWithAgent)} vs{' '}
            {formatMinutes(recordedTiming.minutesByHand)}
          </span>
          <span style={{ color: VERDICT_COLOR[recordedTimingSummary.verdict] }}>
            {recordedTimingSummary.label.split(' · ')[0]}
          </span>
        </span>
      )}
    </>
  ) : null;

  const renderThumb = (sentiment: FeedbackSentiment) => {
    const isActive = submitted?.sentiment === sentiment;
    const iconKind = sentiment === 'positive' ? 'thumbsUp' : 'thumbsDown';

    const ariaLabel =
      sentiment === 'positive' ? 'Helpful answer' : 'Unhelpful answer';

    return (
      <button
        key={sentiment}
        type="button"
        data-testid={`chat-feedback-${
          sentiment === 'positive' ? 'up' : 'down'
        }-button-${index}`}
        aria-label={ariaLabel}
        aria-pressed={isActive}
        aria-disabled={submitted !== null}
        title={ariaLabel}
        onClick={() => handleThumbClick(sentiment)}
        className={cx(
          'tw-flex',
          'tw-items-center',
          'tw-justify-center',
          'tw-shrink-0',
          'tw-w-24',
          'tw-h-24',
          'tw-rounded-6',
          'tw-border-none',
          'tw-bg-transparent',
          'tw-p-0',
        )}
        style={
          {
            // Cursor + color flip with submitted state; transition curve
            // doesn't have a design-system preset.
            cursor: submitted === null ? 'pointer' : 'default',
            [THUMB_COLOR_PROP]: isActive ? ACTIVE_COLOR[sentiment] : IDLE_COLOR,
            color: `var(${THUMB_COLOR_PROP})`,
            transition: 'color 0.15s ease, background 0.15s ease',
          } as React.CSSProperties
        }
        onMouseEnter={(event) => {
          if (submitted !== null) return;
          // eslint-disable-next-line no-param-reassign -- imperative hover background paired below
          event.currentTarget.style.background = 'var(--fill-primary)';
        }}
        onMouseLeave={(event) => {
          // eslint-disable-next-line no-param-reassign -- imperative hover background paired below
          event.currentTarget.style.background = 'transparent';
        }}
      >
        <Icon
          kind={iconKind}
          // 14×14 isn't in the icon-size scale.
          style={{ width: 14, height: 14, color: 'currentColor' }}
        />
      </button>
    );
  };

  const isPositive = pending === 'positive';
  const iconKind = isPositive ? 'thumbsUp' : 'thumbsDown';

  const title = isPositive
    ? 'What did you like about this answer?'
    : 'What was wrong with this answer?';

  const placeholder = isPositive
    ? 'Tell us what worked well — accuracy, clarity, the tools we picked…'
    : 'Tell us what we got wrong — missing steps, wrong info, confusing wording…';

  return (
    <>
      <div ref={rowRef} className="tw-flex tw-flex-col tw-gap-4 tw-mt-6">
        {isTimingOpen && timing?.canLog && recordedTiming === null && (
          // In flow, above the thumbs: the reply text moves up to make room
          // rather than being covered, and the card stays on screen at the
          // bottom of the transcript.
          <div ref={revealOnMount} data-testid="chat-timing-popover">
            <TaskTimingPanel
              index={index}
              suggestedMinutes={timing.suggestedMinutes}
              onLog={handleTimingLog}
              onCancel={() => {
                setIsTimingOpen(false);
                timing.onOpenChange?.(false);
              }}
            />
          </div>
        )}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-min-w-0">
          {renderThumb('positive')}
          {renderThumb('negative')}
          {!hasTrailing && timingControls}
          {submitted === null && quote !== null && (
            // Preview of the selection the rating will carry. Sits where the
            // recorded reason will, so the row does not jump on submit.
            <span
              data-testid={`chat-feedback-quote-preview-${index}`}
              className={cx(
                'tw-ml-6',
                'tw-inline-flex',
                'tw-items-center',
                'tw-gap-4',
                'tw-overflow-hidden',
              )}
              style={{
                fontSize: 12,
                color: 'var(--label-tertiary)',
                borderLeft: '2px solid var(--label-quaternary)',
                paddingLeft: 6,
                maxWidth: 240,
              }}
              title={quote}
            >
              <span className="tw-italic tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                “{elide(quote, QUOTE_PREVIEW_CHARS)}”
              </span>
              <button
                type="button"
                aria-label="Clear quoted text"
                data-testid={`chat-feedback-quote-clear-button-${index}`}
                onClick={() => setQuote(null)}
                className="tw-border-none tw-bg-transparent tw-p-0 tw-cursor-pointer"
                style={{ color: 'var(--label-quaternary)', lineHeight: 1 }}
              >
                ×
              </button>
            </span>
          )}
          {submitted?.quote && (
            // The selection the recorded rating carries, in the same slot
            // the pending preview used, so it survives submit and reload.
            <span
              data-testid={`chat-feedback-quote-recorded-${index}`}
              className={cx(
                'tw-ml-6',
                'tw-italic',
                'tw-overflow-hidden',
                'tw-text-ellipsis',
                'tw-whitespace-nowrap',
              )}
              style={{
                fontSize: 12,
                color: 'var(--label-tertiary)',
                borderLeft: '2px solid var(--label-quaternary)',
                paddingLeft: 6,
                maxWidth: 240,
              }}
              title={submitted.quote}
            >
              “{elide(submitted.quote, QUOTE_PREVIEW_CHARS)}”
            </span>
          )}
          {submitted?.tags && submitted.tags.length > 0 && (
            <span
              data-testid={`chat-feedback-tags-text-${index}`}
              className="tw-ml-6 tw-min-w-0 tw-break-words"
              style={{ fontSize: 12, color: 'var(--label-secondary)' }}
            >
              {submitted.tags.join(' · ')}
            </span>
          )}
          {submitted?.reason && (
            <span
              data-testid={`chat-feedback-reason-text-${index}`}
              className={cx(
                'tw-ml-6',
                'tw-italic',
                'tw-overflow-hidden',
                'tw-text-ellipsis',
                'tw-whitespace-nowrap',
              )}
              style={{
                // 12px font + 40%-black color + 220px max-width aren't in
                // the typography / spacing scales.
                fontSize: 12,
                color: 'var(--label-tertiary)',
                maxWidth: 220,
              }}
              title={submitted.reason}
            >
              “{submitted.reason}”
            </span>
          )}
        </div>
        {hasTrailing && timingControls && (
          // A quote or a recorded verdict already fills the thumbs row;
          // the clock gets its own line so the two do not run together.
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-min-w-0">
            {timingControls}
          </div>
        )}
      </div>

      {pending !== null &&
        renderModalIntoPanel(
          portalTarget,
          <div
            // Modal is portalled into the chat card itself (see
            // `CHAT_PANEL_CARD_ID`) so `position: absolute; inset: 0`
            // aligns to the card regardless of where MessageFeedback
            // is rendered in the DOM (e.g. inside a scrollable +
            // clipped MessageList). Avoids the offset / clipping bugs
            // that surface with `position: fixed` + viewport coords.
            className={cx(
              'tw-absolute',
              'tw-inset-0',
              'tw-flex',
              'tw-items-center',
              'tw-justify-center',
              'tw-p-20',
            )}
            style={{
              // z-index 100 stacks the modal above the panel; backdrop
              // dim color stays inline; animation reuses the chat
              // overlay's keyframe when injected (no-op otherwise).
              zIndex: 100,
              background: 'rgba(0,0,0,0.45)',
              animation: '_chatBackdropIn 0.18s ease forwards',
            }}
            onClick={(e) => {
              // Stop propagation so the click doesn't bubble up
              // through the React portal tree to `ChatPanelOverlay`'s
              // backdrop and dismiss the whole chat panel.
              e.stopPropagation();
              handleCancel();
            }}
            role="presentation"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              // The modal testids carry no index: unlike the thumbs, at most
              // one modal is open at a time (opening one row's modal covers
              // the panel, and clicking the backdrop to reach another row
              // cancels it), so an index would only make them harder to
              // locate.
              data-testid="chat-feedback-reason-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Feedback"
              className={cx(
                'tw-w-full',
                'tw-max-w-320',
                'tw-rounded-14',
                'tw-p-16',
                'tw-flex',
                'tw-flex-col',
                'tw-gap-12',
              )}
              style={{
                background: 'var(--surface-primary-elevated)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
              }}
            >
              <div className="tw-flex tw-items-center tw-gap-10">
                <div
                  className={cx(
                    'tw-flex',
                    'tw-items-center',
                    'tw-justify-center',
                    'tw-w-32',
                    'tw-h-32',
                    'tw-rounded-full',
                    'tw-shrink-0',
                  )}
                  style={{
                    // Static 12% washes of the light-mode verdict tokens
                    // (green.60 #009947, red.60 #F72D2D). Literals, not
                    // var() — a bare token can't carry an alpha — so they
                    // don't track --green/--red into dark mode or the
                    // redesign, where those resolve to other steps.
                    background: isPositive
                      ? 'rgba(0, 153, 71, 0.12)'
                      : 'rgba(247, 45, 45, 0.12)',
                  }}
                >
                  <Icon
                    kind={iconKind}
                    className="tw-w-16 tw-h-16"
                    style={{
                      color: isPositive ? 'var(--green)' : 'var(--red)',
                    }}
                  />
                </div>
                <div
                  style={{
                    // 14/600 + `-0.2` letter-spacing aren't in the
                    // typography scale.
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--label-primary)',
                    letterSpacing: -0.2,
                  }}
                >
                  {title}
                </div>
              </div>

              {quote !== null && (
                <FeedbackQuoteBlock
                  quote={quote}
                  isPositive={isPositive}
                  onClear={() => setQuote(null)}
                />
              )}

              <div
                role="group"
                aria-label="Tags"
                data-testid="chat-feedback-tag-row"
                className="tw-flex tw-flex-wrap tw-gap-6"
              >
                {(isPositive ? POSITIVE_TAGS : NEGATIVE_TAGS).map((tag) => {
                  const isOn = tags.includes(tag);

                  return (
                    <button
                      key={tag}
                      type="button"
                      aria-pressed={isOn}
                      data-testid={`chat-feedback-tag-${tag
                        .replace(/\s+/g, '-')
                        .toLowerCase()}-button`}
                      onClick={() => toggleTag(tag)}
                      className={cx(
                        'tw-rounded-full',
                        'tw-border-none',
                        'tw-cursor-pointer',
                        'tw-whitespace-nowrap',
                      )}
                      style={{
                        // 11px chip text and 22px height aren't in the
                        // scales; the fills use themeable tokens.
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: 1,
                        minHeight: 22,
                        padding: '0 9px',
                        fontFamily: 'inherit',
                        background: isOn
                          ? 'var(--label-primary)'
                          : 'var(--fill-primary)',
                        color: isOn
                          ? 'var(--surface-primary)'
                          : 'var(--label-secondary)',
                        transition: 'background 0.12s ease, color 0.12s ease',
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>

              <textarea
                ref={textareaRef}
                data-testid="chat-feedback-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={placeholder}
                rows={4}
                className={cx(
                  'tw-w-full',
                  'tw-resize-none',
                  'tw-rounded-10',
                  'tw-py-10',
                  'tw-px-12',
                  'tw-text-13',
                  'tw-outline-none',
                )}
                style={{
                  // 1.5 line-height and box-sizing don't map to
                  // design-system tokens; border / text / fill use
                  // themeable tokens. The fill uses `--fill-primary`, not
                  // `--surface-secondary` (which in dark mode matches the
                  // modal card), so the field stays distinct from the card.
                  border: '1px solid var(--divider-primary)',
                  fontFamily: 'inherit',
                  color: 'var(--label-primary)',
                  background: 'var(--fill-primary)',
                  lineHeight: 1.5,
                  boxSizing: 'border-box',
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />

              <div className="tw-flex tw-justify-end tw-gap-8">
                <button
                  type="button"
                  data-testid="chat-feedback-cancel-button"
                  onClick={handleCancel}
                  className={cx(
                    'tw-py-8',
                    'tw-rounded-full',
                    'tw-border-none',
                    'tw-bg-transparent',
                    'tw-cursor-pointer',
                    'tw-text-13',
                    'tw-font-medium',
                  )}
                  style={{
                    // 14px horizontal padding isn't in the spacing scale.
                    paddingLeft: 14,
                    paddingRight: 14,
                    color: 'var(--label-secondary)',
                    fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="chat-feedback-submit-button"
                  onClick={handleSubmit}
                  className={cx(
                    'tw-py-8',
                    'tw-px-16',
                    'tw-rounded-full',
                    'tw-border-none',
                    'tw-cursor-pointer',
                    'tw-text-13',
                    'tw-text-light',
                  )}
                  style={{
                    // 600 weight isn't in the scale (500 → 700). The
                    // orange fill + light label use themeable tokens.
                    background: 'var(--orange)',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>,
        )}
    </>
  );
}
